import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";

/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
  "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const MAX_DEPOSIT_AMOUNT = 1000000;

/* =========================================================
   HELPERS
========================================================= */

const formatNumber = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 8,
  }).format(number);
};

const Checkout = () => {
  const { id } = useParams();

  /* =========================================================
     STATES
  ========================================================= */

  const [deposit, setDeposit] = useState(null);

  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const [termsAccepted, setTermsAccepted] = useState(false);

  const [depositInput, setDepositInput] = useState({
    amount: "",
    transaction_sender_id: "",
    error_list: {},
  });

  /* =========================================================
     GET PAYMENT / DEPOSIT INFORMATION
  ========================================================= */

  useEffect(() => {
    let mounted = true;

    const getDepositInformation = async () => {
      if (!id) {
        setPageError("Invalid payment gateway.");
        setPageLoading(false);
        return;
      }

      try {
        setPageLoading(true);
        setPageError("");

        const data = {
          key_s: API_KEY,
          payment_id: id,
        };

        const response = await axios.post(
          "/apps-deposit-get",
          data
        );

        if (!mounted) return;

        if (response?.data?.data) {
          setDeposit(response.data.data);
        } else {
          setDeposit(null);
          setPageError(
            "Payment gateway information was not found."
          );
        }
      } catch (error) {
        console.error(
          "Deposit information error:",
          error
        );

        if (!mounted) return;

        setPageError(
          error?.response?.data?.message ||
            "Unable to load payment information. Please try again."
        );
      } finally {
        if (mounted) {
          setPageLoading(false);
        }
      }
    };

    getDepositInformation();

    return () => {
      mounted = false;
    };
  }, [id]);

  /* =========================================================
     VALUES
  ========================================================= */

  const minAmount = useMemo(() => {
    return Number(deposit?.min_amount || 0);
  }, [deposit]);

  const maxAmount = MAX_DEPOSIT_AMOUNT;

  const enteredAmount = Number(
    depositInput.amount || 0
  );

  const currencySymbol =
    deposit?.currency_symbols ||
    deposit?.currency_name ||
    "";

  /* =========================================================
     HANDLE INPUT
  ========================================================= */

  const handleInput = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    /*
     * Amount:
     * allow numbers + decimal only.
     */
    if (name === "amount") {
      newValue = value.replace(/[^0-9.]/g, "");

      /*
       * Prevent multiple decimal points
       */
      const decimalParts = newValue.split(".");

      if (decimalParts.length > 2) {
        newValue =
          decimalParts[0] +
          "." +
          decimalParts.slice(1).join("");
      }
    }

    setDepositInput((previous) => ({
      ...previous,

      [name]: newValue,

      error_list: {
        ...previous.error_list,
        [name]: "",
      },
    }));
  };

  /* =========================================================
     SET AMOUNT FROM QUICK BUTTON
  ========================================================= */

  const setQuickAmount = (amount) => {
    if (!amount || amount <= 0) return;

    setDepositInput((previous) => ({
      ...previous,

      amount: String(amount),

      error_list: {
        ...previous.error_list,
        amount: "",
      },
    }));
  };

  /* =========================================================
     VALIDATION
  ========================================================= */

  const validateForm = () => {
    const errors = {};

    const amount = Number(
      depositInput.amount
    );

    if (!depositInput.amount) {
      errors.amount =
        "Please enter deposit amount.";
    } else if (
      !Number.isFinite(amount) ||
      amount <= 0
    ) {
      errors.amount =
        "Please enter a valid amount.";
    } else if (
      minAmount > 0 &&
      amount < minAmount
    ) {
      errors.amount = `Minimum deposit amount is ${formatNumber(
        minAmount
      )} ${currencySymbol}.`;
    } else if (
      maxAmount > 0 &&
      amount > maxAmount
    ) {
      errors.amount = `Maximum deposit amount is ${formatNumber(
        maxAmount
      )} ${currencySymbol}.`;
    }

    if (
      !depositInput.transaction_sender_id.trim()
    ) {
      errors.transaction_sender_id =
        "Transaction Sender ID is required.";
    }

    if (!termsAccepted) {
      errors.terms =
        "Please accept the terms & conditions.";
    }

    setDepositInput((previous) => ({
      ...previous,
      error_list: errors,
    }));

    return Object.keys(errors).length === 0;
  };

  /* =========================================================
     SUBMIT
  ========================================================= */

  const depositSubmit = async (e) => {
    e.preventDefault();

    if (submitting) {
      return;
    }

    const auth_token =
      localStorage.getItem("auth_token");

    if (!auth_token) {
      swal(
        "Session Expired",
        "Please login again before making a deposit.",
        "warning"
      );

      return;
    }

    if (!validateForm()) {
      return;
    }

    const data = {
      amount: depositInput.amount,

      transaction_sender_id:
        depositInput.transaction_sender_id.trim(),

      key_s: API_KEY,

      gateway_id: id,
    };

    try {
      setSubmitting(true);

      const res = await axios.post(
        "/apps-deposit-sub",
        data
      );

      if (res?.data?.status === "success") {
        await swal(
          "Deposit Submitted",
          res.data.message ||
            "Your deposit request has been submitted successfully.",
          "success"
        );

        setDepositInput({
          amount: "",
          transaction_sender_id: "",
          error_list: {},
        });

        setTermsAccepted(false);
      } else if (
        res?.data?.status === "fail"
      ) {
        swal(
          "Unable to Continue",
          res.data.message ||
            "Your deposit request could not be submitted.",
          "warning"
        );
      } else {
        setDepositInput((previous) => ({
          ...previous,

          error_list:
            res?.data?.validation_error ||
            {},
        }));
      }
    } catch (error) {
      console.error(
        "Deposit submit error:",
        error
      );

      /*
       * Laravel validation
       */
      if (
        error?.response?.status === 422
      ) {
        const validationErrors =
          error?.response?.data?.errors ||
          error?.response?.data
            ?.validation_error ||
          {};

        setDepositInput(
          (previous) => ({
            ...previous,

            error_list:
              validationErrors,
          })
        );

        return;
      }

      swal(
        "Something Went Wrong",
        error?.response?.data?.message ||
          "Unable to submit your deposit. Please try again.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================================================
     QUICK AMOUNTS
  ========================================================= */

  const quickAmounts = useMemo(() => {
    if (!deposit) return [];

    const amounts = [];

    /*
     * Minimum
     */
    if (minAmount > 0) {
      amounts.push(minAmount);
    }

    /*
     * Some useful values between min/max
     */
    if (minAmount > 0) {
      amounts.push(minAmount * 2);
      amounts.push(minAmount * 5);
    }

    /*
     * Max
     */
    if (
      maxAmount > 0 &&
      maxAmount !== minAmount
    ) {
      amounts.push(maxAmount);
    }

    /*
     * Remove duplicates and invalid amounts
     */
    return [...new Set(amounts)]
      .filter((amount) => {
        if (amount <= 0) return false;

        if (
          maxAmount > 0 &&
          amount > maxAmount
        ) {
          return false;
        }

        return true;
      })
      .slice(0, 4);
  }, [
    deposit,
    minAmount,
    maxAmount,
  ]);

  /* =========================================================
     SUBMIT STATUS
  ========================================================= */

  const amountIsValid =
    enteredAmount > 0 &&
    (minAmount <= 0 ||
      enteredAmount >= minAmount) &&
    (maxAmount <= 0 ||
      enteredAmount <= maxAmount);

  const canSubmit =
    deposit &&
    amountIsValid &&
    depositInput.transaction_sender_id.trim() !==
      "" &&
    termsAccepted &&
    !pageLoading &&
    !submitting;

  /* =========================================================
     AREA-BASED LOADING
     Full page remains visible while payment information loads.
  ========================================================= */

  /* =========================================================
     ERROR PAGE
  ========================================================= */

  if (
    !pageLoading &&
    (pageError || !deposit)
  ) {
    return (
      <Fragment>
        <SmartDepositCSS />

        <div className="sd-page" style={{background: "white",borderRadius: "20px",}}>
          <div className="sd-container">

            <div className="sd-error-card">

              <div className="sd-error-icon">
                !
              </div>

              <h3>
                Payment Gateway Unavailable
              </h3>

              <p>
                {pageError ||
                  "Payment information could not be loaded."}
              </p>

              <button
                type="button"
                onClick={() =>
                  window.location.reload()
                }
              >
                Try Again
              </button>

            </div>

          </div>
        </div>
      </Fragment>
    );
  }

  /* =========================================================
     MAIN VIEW
  ========================================================= */

  const viewDeposit = deposit || {};

  return (
    <Fragment>

      <SmartDepositCSS />

      <div className="sd-page">

        <div className="sd-container">



          {/* =================================================
              CONTENT GRID
          ================================================= */}

          <div className="sd-grid">

            {/* =================================================
                LEFT SIDE
            ================================================= */}

            <div>

              {/* ===============================================
                  PAYMENT INFO
              =============================================== */}

              <div className="sd-panel">

                <div className="sd-panel-header">

                  <div className="sd-step">
                    1
                  </div>

                  <div>

                    <h3>
                      Payment Information
                    </h3>

                    <p>
                      Send your payment to the
                      receiver information below.
                    </p>

                  </div>

                </div>

                {pageLoading ? (
                  <div className="sd-payment-info sd-payment-loading">
                    <div className="sd-payment-main">
                      <span className="sd-skeleton-box sd-sk-payment-icon"></span>
                      <div className="sd-sk-payment-copy">
                        <span className="sd-skeleton-line sd-sk-small"></span>
                        <span className="sd-skeleton-line sd-sk-title"></span>
                      </div>
                    </div>
                    <div className="sd-info-divider"></div>
                    <div className="sd-info-block">
                      <span className="sd-skeleton-line sd-sk-label"></span>
                      <span className="sd-skeleton-line sd-sk-receiver"></span>
                    </div>
                    <div className="sd-info-divider"></div>
                    <div className="sd-info-block">
                      <span className="sd-skeleton-line sd-sk-label"></span>
                      <span className="sd-skeleton-line sd-sk-detail"></span>
                      <span className="sd-skeleton-line sd-sk-detail-short"></span>
                    </div>
                  </div>
                ) : (
                <div className="sd-payment-info">

                  <div className="sd-payment-main">

                    <div className="sd-payment-icon">
                      $
                    </div>

                    <div>

                      <small>
                        Payment Method
                      </small>

                      <h4>
                        {viewDeposit.Payment_name ||
                          "Payment"}
                      </h4>

                    </div>

                  </div>

                  <div className="sd-info-divider"></div>

                  <div className="sd-info-block">

                    <span>
                      Payment Receiver
                    </span>

                    <div className="sd-receiver">

                      <strong>
                        {viewDeposit.Payment_receiver ||
                          "Not Available"}
                      </strong>

                      {viewDeposit.Payment_receiver && (
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard
                              ?.writeText(
                                viewDeposit.Payment_receiver
                              )
                              .then(() => {
                                swal(
                                  "Copied",
                                  "Payment receiver copied.",
                                  "success"
                                );
                              })
                              .catch(() => {});
                          }}
                        >
                          Copy
                        </button>
                      )}

                    </div>

                  </div>

                     {viewDeposit.Payment_details && (
                       <>
                         <div className="sd-info-divider"></div>

                         <div className="sd-info-block">

                           <span>
                             Payment Instructions
                           </span>

                           <div className="sd-instruction-wrap">

                             <p>
                               {viewDeposit.Payment_details}
                             </p>

                             <button
                               type="button"
                               className="sd-copy-btn"
                               onClick={() => {
                                 navigator.clipboard
                                   .writeText(viewDeposit.Payment_details)
                                   .then(() => {
                                     swal(
                                       "Copied",
                                       "Payment instructions copied successfully.",
                                       "success"
                                     );
                                   })
                                   .catch(() => {
                                     swal(
                                       "Error",
                                       "Unable to copy payment instructions.",
                                       "error"
                                     );
                                   });
                               }}
                             >
                               Copy
                             </button>

                           </div>

                         </div>
                       </>
                     )}

                </div>
                )}

              </div>

              {/* ===============================================
                  FORM
              =============================================== */}

              <div className="sd-panel sd-form-panel">

                <div className="sd-panel-header">

                  <div className="sd-step">
                    2
                  </div>

                  <div>

                    <h3>
                      Submit Deposit
                    </h3>

                    <p>
                      Enter the amount sent and
                      your transaction sender ID.
                    </p>

                  </div>

                </div>

                <form
                  onSubmit={
                    depositSubmit
                  }
                >

                  {/* ===========================================
                      AMOUNT
                  =========================================== */}

                  <div className="sd-field">

                    <label>
                      Deposit Amount
                    </label>

                    <div
                      className={`sd-amount-input ${
                        depositInput
                          .error_list
                          ?.amount
                          ? "sd-input-error"
                          : ""
                      }`}
                    >

                      <span className="sd-currency">
                        {currencySymbol}
                      </span>

                      <input
                        type="text"
                        inputMode="decimal"
                        name="amount"
                        placeholder={`Enter ${viewDeposit.currency_name || ""} amount`}
                        value={
                          depositInput.amount
                        }
                        onChange={
                          handleInput
                        }
                        autoComplete="off"
                        disabled={pageLoading}
                      />

                    </div>

                    {depositInput
                      .error_list
                      ?.amount && (
                      <span className="sd-error-text">
                        {
                          depositInput
                            .error_list
                            .amount
                        }
                      </span>
                    )}

                    {/* QUICK AMOUNT */}

                    {!pageLoading &&
                      quickAmounts.length >
                      0 && (
                      <div className="sd-quick-area">

                        <small>
                          Quick Amount
                        </small>

                        <div className="sd-quick-list">

                          {quickAmounts.map(
                            (amount) => (
                              <button
                                key={
                                  amount
                                }
                                type="button"
                                className={
                                  Number(
                                    depositInput.amount
                                  ) ===
                                  Number(
                                    amount
                                  )
                                    ? "active"
                                    : ""
                                }
                                onClick={() =>
                                  setQuickAmount(
                                    amount
                                  )
                                }
                              >
                                {formatNumber(
                                  amount
                                )}
                              </button>
                            )
                          )}

                        </div>

                      </div>
                    )}

                  </div>

                  {/* ===========================================
                      TRANSACTION ID
                  =========================================== */}

                  <div className="sd-field">

                    <label>
                      Transaction Sender ID
                    </label>

                    <input
                      type="text"
                      className={`sd-input ${
                        depositInput
                          .error_list
                          ?.transaction_sender_id
                          ? "sd-input-error"
                          : ""
                      }`}
                      name="transaction_sender_id"
                      placeholder="Enter transaction sender ID"
                      value={
                        depositInput
                          .transaction_sender_id
                      }
                      onChange={
                        handleInput
                      }
                      autoComplete="off"
                    disabled={pageLoading}
                      />

                    <span className="sd-help">
                      Enter the sender number,
                      account, wallet or
                      transaction identifier used
                      for this payment.
                    </span>

                    {depositInput
                      .error_list
                      ?.transaction_sender_id && (
                      <span className="sd-error-text">
                        {
                          depositInput
                            .error_list
                            .transaction_sender_id
                        }
                      </span>
                    )}

                  </div>

                  {/* ===========================================
                      TERMS
                  =========================================== */}

                  <div className="sd-terms">

                    <input
                      type="checkbox"
                      id="depositTerms"
                      checked={
                        termsAccepted
                      }
                      onChange={(e) => {
                        setTermsAccepted(
                          e.target.checked
                        );

                        setDepositInput(
                          (previous) => ({
                            ...previous,

                            error_list: {
                              ...previous.error_list,
                              terms: "",
                            },
                          })
                        );
                      }}
                    />

                    <label htmlFor="depositTerms">

                      I confirm that the
                      payment information and
                      transaction details are
                      correct, and I accept the
                      terms &amp; conditions.

                    </label>

                  </div>

                  {depositInput
                    .error_list
                    ?.terms && (
                    <span className="sd-error-text">
                      {
                        depositInput
                          .error_list
                          .terms
                      }
                    </span>
                  )}

                  {/* ===========================================
                      SUBMIT
                  =========================================== */}

                  <button
                    type="submit"
                    className="sd-submit"
                    disabled={
                      !canSubmit
                    }
                  >

                    {submitting ? (
                      <>
                        <span className="sd-btn-loader"></span>
                        Processing Deposit...
                      </>
                    ) : (
                      <>
                        Continue to Deposit
                        <span>
                          →
                        </span>
                      </>
                    )}

                  </button>

                </form>

              </div>

            </div>

            {/* =================================================
                RIGHT SUMMARY
            ================================================= */}

            <aside className="sd-summary">

              <div className="sd-summary-head">

                <small>
                  Deposit Overview
                </small>

                <h3>
                  Transaction Summary
                </h3>

              </div>

              <div className="sd-summary-body">

                {/* PAYMENT */}

                <div className="sd-selected-payment">

                  <div className="sd-selected-icon">
                    $
                  </div>

                  <div>

                    <small>
                      Payment Method
                    </small>

                    {pageLoading ? (
                      <span className="sd-skeleton-line sd-sk-summary-method"></span>
                    ) : (
                      <strong>
                        {viewDeposit.Payment_name}
                      </strong>
                    )}

                  </div>

                </div>

                {/* DETAILS */}

                <div className="sd-summary-row">

                  <span>
                    Currency
                  </span>

                  {pageLoading ? (
                    <span className="sd-skeleton-line sd-sk-summary-value"></span>
                  ) : (
                    <strong>
                      {viewDeposit.currency_name ||
                        currencySymbol}
                    </strong>
                  )}

                </div>

                <div className="sd-summary-row">

                  <span>
                    Deposit Allocation
                  </span>

                  <strong>
                    100%
                  </strong>

                </div>

                <div className="sd-summary-row">

                  <span>
                    Minimum Deposit
                  </span>

                  {pageLoading ? (
                    <span className="sd-skeleton-line sd-sk-summary-value"></span>
                  ) : (
                    <strong>
                      {formatNumber(
                        minAmount
                      )}{" "}
                      {currencySymbol}
                    </strong>
                  )}

                </div>

                <div className="sd-summary-row">

                  <span>
                    Maximum Deposit
                  </span>

                  {pageLoading ? (
                    <span className="sd-skeleton-line sd-sk-summary-value"></span>
                  ) : (
                    <strong>
                      {formatNumber(
                        maxAmount
                      )}{" "}
                      {currencySymbol}
                    </strong>
                  )}

                </div>

                {/* TOTAL */}

                <div className="sd-total">

                  <span>
                    Your Deposit
                  </span>

                  <strong>
                    {formatNumber(
                      enteredAmount
                    )}{" "}
                    <small>
                      {currencySymbol}
                    </small>
                  </strong>

                </div>

                {/* LIMIT */}

                <div className="sd-limit-box">

                  <div>
                    !
                  </div>

                  {pageLoading ? (
                    <div className="sd-sk-limit-copy">
                      <span className="sd-skeleton-line sd-sk-detail"></span>
                      <span className="sd-skeleton-line sd-sk-detail-short"></span>
                    </div>
                  ) : (
                    <p>
                      Your deposit must be
                      between{" "}
                      <strong>
                        {formatNumber(
                          minAmount
                        )}{" "}
                        {currencySymbol}
                      </strong>{" "}
                      and{" "}
                      <strong>
                        {formatNumber(
                          maxAmount
                        )}{" "}
                        {currencySymbol}
                      </strong>
                      .
                    </p>
                  )}

                </div>

                {/* STATUS */}

                <div className="sd-status">

                  <span></span>

                  {pageLoading
                    ? "Loading payment gateway..."
                    : "Payment gateway available"}

                </div>

              </div>

            </aside>

          </div>

        </div>

      </div>

    </Fragment>
  );
};

/* =========================================================
   CSS
========================================================= */

const SmartDepositCSS = () => (
  <style>{`

    /* =====================================================
       PAGE
    ===================================================== */

    .sd-page {

      --sd-primary: #635bff;
      --sd-primary-dark: #5148e5;

      --sd-green: #12b76a;

      --sd-red: #f04438;

      --sd-orange: #f79009;

      --sd-text: #182230;

      --sd-muted: #667085;

      --sd-border: #e7e9ef;

      --sd-bg: #f7f8fb;

      min-height: 100vh;

      padding: 10px;

      color:
        var(--sd-text);

      background:

        radial-gradient(
          circle at 0 0,
          rgba(99,91,255,.08),
          transparent 28%
        ),

        radial-gradient(
          circle at 100% 10%,
          rgba(18,183,106,.05),
          transparent 25%
        ),

        var(--sd-bg);

      font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;

    }

    .sd-container {

      width: 100%;

      max-width: 1250px;

      margin: 0 auto;

    }


    /* =====================================================
       HERO
    ===================================================== */

    .sd-hero {

      position: relative;

      overflow: hidden;

      display: flex;

      align-items: center;

      justify-content:
        space-between;

      gap: 25px;

      padding: 30px;

      margin-bottom: 20px;

      border-radius: 24px;

      background:

        linear-gradient(
          135deg,
          #171b2d 0%,
          #252b48 58%,
          #393568 100%
        );

      box-shadow:
        0 18px 50px
        rgba(31,38,65,.12);

    }

    .sd-hero::after {

      content: "";

      position: absolute;

      width: 300px;

      height: 300px;

      right: -90px;

      top: -170px;

      border-radius: 50%;

      background:
        rgba(255,255,255,.07);

    }

    .sd-hero-content {

      position: relative;

      z-index: 2;

      max-width: 650px;

    }

    .sd-hero-badge {

      display: inline-flex;

      align-items: center;

      gap: 8px;

      margin-bottom: 10px;

      color: #cbc8ff;

      font-size: 11px;

      font-weight: 800;

      letter-spacing: .7px;

      text-transform: uppercase;

    }

    .sd-hero-badge span {

      width: 8px;

      height: 8px;

      border-radius: 50%;

      background: #75e5aa;

      box-shadow:
        0 0 0 5px
        rgba(117,229,170,.12);

    }

    .sd-hero h1 {

      margin: 0 0 8px;

      color: #fff;

      font-size: 30px;

      font-weight: 850;

      line-height: 1.2;

    }

    .sd-hero p {

      max-width: 580px;

      margin: 0;

      color:
        rgba(255,255,255,.68);

      font-size: 13px;

      line-height: 1.7;

    }

    .sd-hero-gateway {

      position: relative;

      z-index: 2;

      min-width: 210px;

      padding: 16px 18px;

      border:
        1px solid
        rgba(255,255,255,.12);

      border-radius: 17px;

      background:
        rgba(255,255,255,.07);

      backdrop-filter:
        blur(10px);

    }

    .sd-hero-gateway small {

      display: block;

      margin-bottom: 4px;

      color:
        rgba(255,255,255,.52);

      font-size: 10px;

    }

    .sd-hero-gateway strong {

      display: block;

      color: #fff;

      font-size: 15px;

      font-weight: 800;

    }

    .sd-hero-gateway span {

      display: inline-block;

      margin-top: 8px;

      padding: 4px 9px;

      border-radius: 20px;

      background:
        rgba(117,229,170,.12);

      color: #8aefba;

      font-size: 10px;

      font-weight: 800;

    }


    /* =====================================================
       GRID
    ===================================================== */

    .sd-grid {

      display: grid;

      grid-template-columns:
        minmax(0,1fr)
        350px;

      gap: 20px;

      align-items: start;

    }


    /* =====================================================
       PANEL
    ===================================================== */

    .sd-panel {

      padding: 23px;

      margin-bottom: 20px;

      border:
        1px solid
        var(--sd-border);

      border-radius: 21px;

      background: #fff;

      box-shadow:
        0 8px 30px
        rgba(30,38,64,.05);

    }

    .sd-panel-header {

      display: flex;

      align-items: flex-start;

      gap: 12px;

      margin-bottom: 20px;

    }

    .sd-step {

      flex: 0 0 38px;

      width: 38px;

      height: 38px;

      display: flex;

      align-items: center;

      justify-content: center;

      border-radius: 12px;

      background: #efedff;

      color:
        var(--sd-primary);

      font-size: 14px;

      font-weight: 900;

    }

    .sd-panel-header h3 {

      margin: 0 0 3px;

      color:
        var(--sd-text);

      font-size: 17px;

      font-weight: 850;

    }

    .sd-panel-header p {

      margin: 0;

      color:
        var(--sd-muted);

      font-size: 11px;

      line-height: 1.5;

    }


    /* =====================================================
       PAYMENT INFORMATION
    ===================================================== */

    .sd-payment-info {

      overflow: hidden;

      border:
        1px solid
        #eceef3;

      border-radius: 16px;

      background: #fbfcfd;

    }

    .sd-payment-main {

      display: flex;

      align-items: center;

      gap: 12px;

      padding: 16px;

    }

    .sd-payment-icon {

      flex: 0 0 43px;

      width: 43px;

      height: 43px;

      display: flex;

      align-items: center;

      justify-content: center;

      border-radius: 13px;

      background:
        linear-gradient(
          135deg,
          var(--sd-primary),
          #837cff
        );

      color: #fff;

      font-size: 17px;

      font-weight: 900;

    }

    .sd-payment-main small {

      display: block;

      color: #98a2b3;

      font-size: 10px;

      margin-bottom: 2px;

    }

    .sd-payment-main h4 {

      margin: 0;

      color:
        var(--sd-text);

      font-size: 15px;

      font-weight: 850;

    }

    .sd-info-divider {

      height: 1px;

      background:
        #e9ebf0;

    }

    .sd-info-block {

      padding: 15px 16px;

    }

    .sd-info-block > span {

      display: block;

      margin-bottom: 6px;

      color:
        #98a2b3;

      font-size: 10px;

      font-weight: 700;

      text-transform: uppercase;

      letter-spacing: .4px;

    }

    .sd-info-block p {

      margin: 0;

      color: #475467;

      font-size: 12px;

      line-height: 1.7;

      white-space:
        pre-line;

    }

    .sd-receiver {

      display: flex;

      align-items: center;

      justify-content:
        space-between;

      gap: 15px;

    }

    .sd-receiver strong {

      color:
        var(--sd-text);

      font-size: 15px;

      font-weight: 850;

      word-break:
        break-all;

    }

    .sd-receiver button {

      flex: 0 0 auto;

      padding: 7px 11px;

      border: 0;

      border-radius: 9px;

      background:
        #eeecff;

      color:
        var(--sd-primary);

      font-size: 10px;

      font-weight: 800;

      cursor: pointer;

    }


    /* =====================================================
       FORM
    ===================================================== */

    .sd-field {

      margin-bottom: 20px;

    }

    .sd-field label {

      display: block;

      margin-bottom: 7px;

      color: #344054;

      font-size: 12px;

      font-weight: 800;

    }

    .sd-input {

      width: 100%;

      height: 50px;

      padding: 0 14px;

      border:
        1.5px solid
        #dfe3ea;

      border-radius: 13px;

      outline: none;

      background: #fff;

      color:
        var(--sd-text);

      font-size: 13px;

      font-weight: 600;

      transition: .2s;

    }

    .sd-input:focus {

      border-color:
        var(--sd-primary);

      box-shadow:
        0 0 0 4px
        rgba(99,91,255,.08);

    }

    .sd-amount-input {

      display: flex;

      align-items: center;

      height: 52px;

      overflow: hidden;

      border:
        1.5px solid
        #dfe3ea;

      border-radius: 13px;

      background: #fff;

      transition: .2s;

    }

    .sd-amount-input:focus-within {

      border-color:
        var(--sd-primary);

      box-shadow:
        0 0 0 4px
        rgba(99,91,255,.08);

    }

    .sd-currency {

      height: 100%;

      min-width: 60px;

      display: flex;

      align-items: center;

      justify-content: center;

      padding: 0 12px;

      border-right:
        1px solid
        #e8eaf0;

      background: #f7f8fb;

      color:
        var(--sd-primary);

      font-size: 12px;

      font-weight: 850;

    }

    .sd-amount-input input {

      width: 100%;

      height: 100%;

      padding: 0 14px;

      border: 0;

      outline: 0;

      background:
        transparent;

      color:
        var(--sd-text);

      font-size: 15px;

      font-weight: 750;

    }

    .sd-input-error {

      border-color:
        var(--sd-red)
        !important;

    }

    .sd-error-text {

      display: block;

      margin-top: 6px;

      color:
        var(--sd-red);

      font-size: 10px;

      font-weight: 650;

    }

    .sd-help {

      display: block;

      margin-top: 6px;

      color: #98a2b3;

      font-size: 10px;

      line-height: 1.5;

    }

   .sd-instruction-wrap {
     display: flex;
     align-items: flex-start;
     justify-content: space-between;
     gap: 15px;
   }

   .sd-instruction-wrap p {
     flex: 1;
     margin: 0;
     color: #475467;
     font-size: 12px;
     line-height: 1.7;
     white-space: pre-line;
     word-break: break-word;
   }

   .sd-copy-btn {
     flex: 0 0 auto;
     padding: 7px 13px;
     border: 0;
     border-radius: 9px;
     background: #eeecff;
     color: #635bff;
     font-size: 10px;
     font-weight: 800;
     cursor: pointer;
     transition: 0.2s ease;
   }

   .sd-copy-btn:hover {
     background: #635bff;
     color: #fff;
   }

   @media (max-width: 575px) {
     .sd-instruction-wrap {
       flex-direction: column;
     }

     .sd-copy-btn {
       width: 100%;
     }
   }
    /* =====================================================
       QUICK AMOUNT
    ===================================================== */

    .sd-quick-area {

      margin-top: 10px;

    }

    .sd-quick-area > small {

      display: block;

      margin-bottom: 7px;

      color: #98a2b3;

      font-size: 9px;

      font-weight: 700;

    }

    .sd-quick-list {

      display: flex;

      flex-wrap: wrap;

      gap: 7px;

    }

    .sd-quick-list button {

      padding: 6px 11px;

      border:
        1px solid
        #e3e5ea;

      border-radius: 9px;

      background: #fff;

      color: #475467;

      font-size: 10px;

      font-weight: 750;

      cursor: pointer;

      transition: .15s;

    }

    .sd-quick-list button:hover,
    .sd-quick-list button.active {

      border-color:
        var(--sd-primary);

      background:
        #efedff;

      color:
        var(--sd-primary);

    }


    /* =====================================================
       TERMS
    ===================================================== */

    .sd-terms {

      display: flex;

      align-items:
        flex-start;

      gap: 10px;

      margin-top: 5px;

      padding: 13px;

      border:
        1px solid
        #e7e9ef;

      border-radius: 13px;

      background:
        #f8f9fb;

    }

    .sd-terms input {

      flex: 0 0 17px;

      width: 17px;

      height: 17px;

      margin-top: 2px;

      accent-color:
        var(--sd-primary);

      cursor: pointer;

    }

    .sd-terms label {

      margin: 0;

      color: #475467;

      font-size: 10px;

      line-height: 1.6;

      cursor: pointer;

    }


    /* =====================================================
       SUBMIT
    ===================================================== */

    .sd-submit {

      width: 100%;

      min-height: 52px;

      display: flex;

      align-items: center;

      justify-content: center;

      gap: 9px;

      margin-top: 17px;

      border: 0;

      border-radius: 13px;

      background:

        linear-gradient(
          135deg,
          var(--sd-primary),
          var(--sd-primary-dark)
        );

      color: #fff;

      font-size: 13px;

      font-weight: 850;

      cursor: pointer;

      box-shadow:
        0 10px 22px
        rgba(99,91,255,.20);

      transition: .2s;

    }

    .sd-submit:hover:not(:disabled) {

      transform:
        translateY(-1px);

      box-shadow:
        0 14px 28px
        rgba(99,91,255,.25);

    }

    .sd-submit:disabled {

      cursor:
        not-allowed;

      opacity: .52;

      box-shadow: none;

    }

    .sd-btn-loader {

      width: 15px;

      height: 15px;

      border:
        2px solid
        rgba(255,255,255,.35);

      border-top-color: #fff;

      border-radius: 50%;

      animation:
        sd-spin .7s linear
        infinite;

    }

    @keyframes sd-spin {

      to {

        transform:
          rotate(360deg);

      }

    }


    /* =====================================================
       SUMMARY
    ===================================================== */

    .sd-summary {

      position: sticky;

      top: 20px;

      overflow: hidden;

      border:
        1px solid
        var(--sd-border);

      border-radius: 21px;

      background: #fff;

      box-shadow:
        0 8px 30px
        rgba(30,38,64,.05);

    }

    .sd-summary-head {

      padding: 20px;

      background:

        linear-gradient(
          135deg,
          #171b2d,
          #2a304d
        );

    }

    .sd-summary-head small {

      display: block;

      margin-bottom: 4px;

      color:
        rgba(255,255,255,.55);

      font-size: 10px;

    }

    .sd-summary-head h3 {

      margin: 0;

      color: #fff;

      font-size: 17px;

      font-weight: 850;

    }

    .sd-summary-body {

      padding: 18px 20px;

    }

    .sd-selected-payment {

      display: flex;

      align-items: center;

      gap: 11px;

      margin-bottom: 15px;

      padding: 12px;

      border:
        1px solid
        #e9e7ff;

      border-radius: 13px;

      background:
        #f8f7ff;

    }

    .sd-selected-icon {

      flex: 0 0 37px;

      width: 37px;

      height: 37px;

      display: flex;

      align-items: center;

      justify-content: center;

      border-radius: 11px;

      background:
        var(--sd-primary);

      color: #fff;

      font-size: 14px;

      font-weight: 900;

    }

    .sd-selected-payment small {

      display: block;

      margin-bottom: 2px;

      color:
        var(--sd-muted);

      font-size: 9px;

    }

    .sd-selected-payment strong {

      display: block;

      color:
        var(--sd-text);

      font-size: 12px;

      font-weight: 850;

    }

    .sd-summary-row {

      display: flex;

      align-items: center;

      justify-content:
        space-between;

      gap: 15px;

      padding: 11px 0;

      border-bottom:
        1px solid #eef0f3;

    }

    .sd-summary-row span {

      color:
        var(--sd-muted);

      font-size: 10px;

    }

    .sd-summary-row strong {

      color:
        var(--sd-text);

      font-size: 10px;

      font-weight: 800;

      text-align: right;

    }

    .sd-total {

      padding: 18px 0 5px;

    }

    .sd-total > span {

      display: block;

      margin-bottom: 5px;

      color:
        var(--sd-muted);

      font-size: 10px;

    }

    .sd-total > strong {

      display: block;

      color:
        var(--sd-primary);

      font-size: 25px;

      font-weight: 900;

      word-break:
        break-word;

    }

    .sd-total strong small {

      font-size: 11px;

      font-weight: 750;

    }


    /* =====================================================
       LIMIT
    ===================================================== */

    .sd-limit-box {

      display: flex;

      align-items:
        flex-start;

      gap: 9px;

      margin-top: 15px;

      padding: 11px;

      border:
        1px solid
        #ffebc2;

      border-radius: 11px;

      background:
        #fff9ed;

    }

    .sd-limit-box > div {

      flex: 0 0 23px;

      width: 23px;

      height: 23px;

      display: flex;

      align-items: center;

      justify-content: center;

      border-radius: 50%;

      background:
        var(--sd-orange);

      color: #fff;

      font-size: 10px;

      font-weight: 900;

    }

    .sd-limit-box p {

      margin: 0;

      color: #8a5a00;

      font-size: 9px;

      line-height: 1.6;

    }


    /* =====================================================
       STATUS
    ===================================================== */

    .sd-status {

      display: flex;

      align-items: center;

      gap: 7px;

      margin-top: 14px;

      color: #47705a;

      font-size: 9px;

      font-weight: 700;

    }

    .sd-status span {

      width: 7px;

      height: 7px;

      border-radius: 50%;

      background:
        var(--sd-green);

      box-shadow:
        0 0 0 4px
        rgba(18,183,106,.10);

    }


    /* =====================================================
       AREA-BASED API LOADING
       Page stays visible; only API-dependent areas shimmer.
    ===================================================== */

    .sd-skeleton-line,
    .sd-skeleton-box {
      display: block;
      position: relative;
      overflow: hidden;
      background: #eceef3;
    }

    .sd-skeleton-line {
      border-radius: 999px;
    }

    .sd-skeleton-box {
      border-radius: 12px;
    }

    .sd-skeleton-line::after,
    .sd-skeleton-box::after {
      content: "";
      position: absolute;
      inset: 0;
      transform: translateX(-100%);
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,.88),
        transparent
      );
      animation: sd-area-shimmer 1.2s infinite;
    }

    @keyframes sd-area-shimmer {
      100% {
        transform: translateX(100%);
      }
    }

    .sd-sk-hero-name {
      width: 145px;
      height: 16px;
      margin-top: 4px;
      background: rgba(255,255,255,.14);
    }

    .sd-sk-hero-currency {
      width: 72px;
      height: 23px;
      margin-top: 9px;
      background: rgba(117,229,170,.12);
    }

    .sd-sk-payment-icon {
      flex: 0 0 43px;
      width: 43px;
      height: 43px;
    }

    .sd-sk-payment-copy {
      flex: 1;
    }

    .sd-sk-small {
      width: 90px;
      height: 10px;
      margin-bottom: 8px;
    }

    .sd-sk-title {
      width: min(190px, 60%);
      height: 16px;
    }

    .sd-sk-label {
      width: 105px;
      height: 10px;
      margin-bottom: 10px;
    }

    .sd-sk-receiver {
      width: min(330px, 75%);
      height: 16px;
    }

    .sd-sk-detail {
      width: 92%;
      height: 11px;
      margin-bottom: 8px;
    }

    .sd-sk-detail-short {
      width: 62%;
      height: 11px;
    }

    .sd-sk-summary-method {
      width: 115px;
      height: 13px;
      margin-top: 4px;
    }

    .sd-sk-summary-value {
      width: 82px;
      height: 12px;
      margin-left: auto;
    }

    .sd-sk-limit-copy {
      flex: 1;
      padding-top: 3px;
    }

    .sd-payment-loading {
      min-height: 235px;
    }

    /* =====================================================
       LOADING / ERROR
    ===================================================== */

    .sd-loading-card,
    .sd-error-card {

      max-width: 500px;

      margin: 70px auto;

      padding: 38px 25px;

      border:
        1px solid
        var(--sd-border);

      border-radius: 21px;

      background: #fff;

      text-align: center;

      box-shadow:
        0 10px 35px
        rgba(30,38,64,.06);

    }

    .sd-loading-icon {

      width: 55px;

      height: 55px;

      display: flex;

      align-items: center;

      justify-content: center;

      margin:
        0 auto 14px;

      border-radius: 16px;

      background:
        #efedff;

    }

    .sd-loading-icon span {

      width: 22px;

      height: 22px;

      border:
        3px solid
        #d8d4ff;

      border-top-color:
        var(--sd-primary);

      border-radius: 50%;

      animation:
        sd-spin .7s
        linear infinite;

    }

    .sd-loading-card h4,
    .sd-error-card h3 {

      margin: 0 0 6px;

      color:
        var(--sd-text);

      font-size: 17px;

      font-weight: 850;

    }

    .sd-loading-card p,
    .sd-error-card p {

      margin: 0;

      color:
        var(--sd-muted);

      font-size: 11px;

      line-height: 1.6;

    }

    .sd-error-icon {

      width: 52px;

      height: 52px;

      display: flex;

      align-items: center;

      justify-content: center;

      margin:
        0 auto 14px;

      border-radius: 15px;

      background:
        #fff0ee;

      color:
        var(--sd-red);

      font-size: 20px;

      font-weight: 900;

    }

    .sd-error-card button {

      margin-top: 16px;

      padding: 9px 17px;

      border: 0;

      border-radius: 10px;

      background:
        var(--sd-primary);

      color: #fff;

      font-size: 11px;

      font-weight: 800;

      cursor: pointer;

    }


    /* =====================================================
       TABLET
    ===================================================== */

    @media (
      max-width: 1050px
    ) {

      .sd-grid {

        grid-template-columns:
          1fr;

      }

      .sd-summary {

        position: static;

      }

    }


    /* =====================================================
       MOBILE
    ===================================================== */

    @media (
      max-width: 575px
    ) {

      .sd-page {

        padding: 12px;

      }

      .sd-hero {

        flex-direction:
          column;

        align-items:
          flex-start;

        padding:
          22px 18px;

        border-radius:
          18px;

      }

      .sd-hero h1 {

        font-size:
          23px;

      }

      .sd-hero p {

        font-size:
          11px;

      }

      .sd-hero-gateway {

        width: 100%;

        min-width: 0;

      }

      .sd-panel {

        padding: 17px;

        border-radius:
          17px;

      }

      .sd-panel-header h3 {

        font-size:
          15px;

      }

      .sd-receiver {

        align-items:
          flex-start;

        flex-direction:
          column;

      }

      .sd-receiver button {

        width: 100%;

      }

      .sd-summary {

        border-radius:
          17px;

      }

      .sd-total > strong {

        font-size:
          21px;

      }

    }

  `}</style>
);

export default Checkout;